import type { ReactNode } from 'react';
import { cn } from '@/lib/utils';

/**
 * AppShell: the persistent frame. A fixed rail on the left, a fixed top bar
 * across the rest, and one scroll-owning content column inset by the rail.
 *
 * All geometry reads CSS vars from @latisent/brand/theme.css
 * (`--app-content-inset`, `--app-topnav-h`), which the sidebar drives by
 * stamping `data-sidebar-mode` on <html>. Nothing here, and nothing in any
 * consumer, may write a pixel width.
 */
export function AppShell({
  sidebar,
  topBar,
  children,
  className,
}: {
  /** The rail (AppSidebar via an adapter). It positions itself. */
  sidebar: ReactNode;
  /** Contents of the fixed top bar. Omit for a shell with no top bar. */
  topBar?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  return (
    <div className={cn('flex h-dvh overflow-hidden bg-background', className)}>
      {sidebar}

      {topBar && (
        <header
          // Shell chrome: same layer as the rail. The rail's logo block
          // (z-shell-logo) paints over the top bar's left gutter by design.
          className="fixed right-0 top-0 z-shell border-b border-sidebar-border bg-sidebar transition-[left] duration-200 ease-out"
          style={{ left: 'var(--app-content-inset)', height: 'var(--app-topnav-h)' }}
        >
          {/* pl-14 on mobile leaves room for the drawer button the rail pins top-left. */}
          <div className="flex h-full w-full min-w-0 items-center gap-2 pl-14 pr-3 md:gap-3 md:px-4 lg:px-6">{topBar}</div>
        </header>
      )}

      <div
        className="flex min-w-0 flex-1 flex-col overflow-hidden transition-[margin-left] duration-200 ease-out"
        style={{ marginLeft: 'var(--app-content-inset)' }}
      >
        <main
          id="main"
          className={cn('flex h-full min-w-0 flex-1 flex-col overflow-hidden', topBar && 'pt-[var(--app-topnav-h)]')}
        >
          {children}
        </main>
      </div>
    </div>
  );
}
